/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { FunctionComponent, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useEventSubscription, IEvent } from '@pwc/event-hub-ui';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { PROJECT_SOURCE } from 'features/project/projectUtil';

import { analysesGroupsListPath } from 'common/services/urlService';
import userTokenService from 'common/services/security/userTokenService';

import {
  geKFAtNavigationLink,
  getNavigationLink,
} from 'features/analysesGroups/utils/groupsUtilities';
import { CONTENT_SUBTYPES } from 'common/engine/utilities/constants';
import FederatedMFE from 'common/components/FederatedMFE';
import featureFlags from 'common/helpers/featureFlags';
import {
  getDoNotCloneVisualization,
  getProject,
  getProjectSource,
} from 'features/project/projectSlice';
import { getLoggedInUser } from 'common/redux/security/loggedInUser/loggedInUserSelector';
import {
  isProjectArchived,
  isProjectInactive,
} from 'common/helpers/projectStatusesUtils';
import { injectReducerAsync } from '../../app/reduxStore';

import styles from './ContentHubPage.module.scss';
import { Project } from '../../types/project';

type getLoggedInUserType = {
  familyName: string;
  givenName: string;
  initials: string;
  name: string;
};

const ContentHubPage: FunctionComponent = () => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { projectId } = useParams<{
    projectId: string;
  }>();
  const project: Project = useSelector(getProject);
  const loggedInUser = useSelector(getLoggedInUser) as getLoggedInUserType;
  const isLiveLinkOn = useSelector(getDoNotCloneVisualization);
  const history = useHistory();
  const isReadOnlyUser = userTokenService.isPwcReadOnlyRole(projectId);
  const isClientUser = !userTokenService.isUserInternal();
  const projectSource = useSelector(getProjectSource);

  const { isEnabledFileFoldersFeature } = featureFlags;

  /**
   * Subscribe to events on project overview topic click to go to that detail
   */
  useEventSubscription(
    'contenthub-po-topic-redirect',
    (
      event: IEvent<{
        threadId: string;
      }>
    ) => {
      if (event.data?.threadId) {
        history.push(`/projects/${projectId}#${event.data.threadId}`);
      }
    }
  );

  /**
   * Subscribe to events on key focus area click to go to that detail
   */
  useEventSubscription(
    'contenthub-kfa-redirect',
    (
      event: IEvent<{
        threadId: string;
      }>
    ) => {
      if (event.data?.threadId) {
        history.push(
          geKFAtNavigationLink(Number(projectId), Number(event.data.threadId))
        );
      }
    }
  );

  /**
   * Subscribe to events on analyses click to go to that detail
   */
  useEventSubscription(
    'contenthub-analysis-redirect',
    (
      event: IEvent<{
        threadId: string;
        subType?: number;
        isNestedAnalysis?: boolean;
        isAnalysisGroup?: boolean;
      }>
    ) => {
      if (event?.data?.isAnalysisGroup) {
        history.push(analysesGroupsListPath(Number(projectId)));
        return;
      }

      if (event.data?.threadId) {
        history.push(
          getNavigationLink(
            Number(projectId),
            Number(event.data.threadId),
            event.data?.subType || CONTENT_SUBTYPES.UNSTRUCTURED,
            0,
            isPwcUser
          )
        );
      }
    }
  );

  /**
   * Subscribe to events on files click to go to that detail
   */
  useEventSubscription(
    'contenthub-file-redirect',
    (
      event: IEvent<{
        fileId?: string;
      }>
    ) => {
      if (isEnabledFileFoldersFeature) {
        history.push(`/projects/${projectId}/files/folders`);
      } else if (event.data?.fileId) {
        history.push(`/projects/${projectId}/files/#${event.data.fileId}`);
      }
    }
  );

  const extraProps = useMemo(
    () => ({
      projectId,
      disabledSections: {
        files: project?.EnableFileFeature !== 'true',
      },
      isProjectArchived: isProjectArchived(project?.status),
      isProjectInactive: isProjectInactive(project?.status),
      isLiveLinkOn,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      loggedInUser: loggedInUser?.name ? loggedInUser.name : '',
      isReadOnlyUser,
      userName: loggedInUser?.name,
      isClientUser,
      projectGuid: project?.guid,
      projectTerritory: project?.territory,
      isPOTopicRefactorEnabled: featureFlags.isPOTopicRefactorEnabled,
      navigation: project?.navigation,
      isTpsEnabledForProject: projectSource === PROJECT_SOURCE.CDE,
      users: project?.users,
    }),
    [
      projectId,
      project?.EnableFileFeature,
      project?.status,
      isLiveLinkOn,
      isReadOnlyUser,
      loggedInUser,
      isClientUser,
      project?.guid,
      project?.territory,
      project?.navigation,
      projectSource,
      project?.users,
    ]
  );

  return (
    <div className={styles['content-hub--wrapper']}>
      <FederatedMFE
        urlKey="REACT_APP_CONTENT_HUB_MFE_URL"
        scope="remoteapp"
        reducerModule="./ContentHubReducers"
        componentModule="./ContentHub"
        injectionReducerCb={injectReducerAsync}
        suspenseFallbackStyle={styles['content-hub-loader-wrapper']}
        props={extraProps}
      />
    </div>
  );
};

export default ContentHubPage;
